import * as Sentry from "@sentry/nextjs"
import React, { useEffect, useMemo } from "react"
import Head from "next/head"
import styles from "../../../styles/Index.module.css"
import { DanjiView, DanjiProps } from "@modules/tourdanji/react"
import { useSearchParams } from "next/navigation"
import { LoadingPanel } from "@modules/tourdanji/src/react/LoadingPanel"
import { initializePinpoint } from "@modules/tourdanji/src/react/utils/PinPoint"
import { Resolution } from "@modules/babylonjs/src/QualitySettings"
import { getQualitySettings } from "@modules/babylonjs"

// 타입 안전 유틸리티 함수 정의
const parseOptionalInt = (value: string | null | undefined): number | undefined => {
  if (typeof value !== "string") return undefined;
  const n = Number.parseInt(value, 10);
  return Number.isNaN(n) ? undefined : n;
};
const parseOptionalBool = (value: string | null | undefined): boolean | undefined => {
  if (value === null || value === undefined) return undefined;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};
const parseOptionalEnum = <T extends string>(value: string | null | undefined, valid: readonly T[]): T | undefined => {
  if (typeof value !== "string") return undefined;
  return valid.includes(value as T) ? (value as T) : undefined;
};

export interface IndexProps {
  onLoad?: () => void
}

export function MainViewer({ onLoad }: IndexProps) {
  const searchParams = useSearchParams()
  const qualitySettings = getQualitySettings();
  qualitySettings.resolution = process.env['TARGET_RESOLUTION_HEIGHT'] as unknown as Resolution;
  const danjiViewProps = useMemo<DanjiProps>(
    () => ({
      danjiId: searchParams.get("danjiId")!,
      appVersion: searchParams.get("appVersion") ?? undefined,
      stage: searchParams.get("stage") ?? process.env["STAGE"],
      deviceInfo: {
        platform: searchParams.get("platform") ?? undefined,
        openglVersion: searchParams.get("openglVersion") ?? undefined,
        deviceMemory: searchParams.get("deviceMemory") ? Number.parseInt(searchParams.get("deviceMemory")!) : undefined,
        gpuModel: searchParams.get("gpuModel") ?? undefined,
        modelNumber: searchParams.get("modelNumber") ?? undefined,
      },
      babylonjsOptions: {
        apiEndpoint: process.env["API_URL"] ?? "/",
        assetsEndpoint: process.env["ASSETS_URL"] ?? "/",
        resourceEndpoint: process.env["RESOURCE_URL"] ?? "/",
        metadataVersion: "8",
        qualitySettings: qualitySettings,
      },
      danjiOptions: {
        zamParams: {
          screen_name: parseOptionalEnum(searchParams.get("screen_name"), ["ZED매물상세", "ZED상세", "ZED매물카탈로그"] as const) as "ZED매물상세" | "ZED상세" | "ZED매물카탈로그",
          item_type: searchParams.get("item_type") ?? undefined,
          ho_id: searchParams.get("ho_id") ?? undefined,
          deal_type: parseOptionalEnum(searchParams.get("deal_type"), ["매매", "전세", "월세"] as const),
          area_danji_id: searchParams.get("danjiId") ?? "",
          dong: searchParams.get("dong") ?? "",
          ho: searchParams.get("ho") ?? "",
        },
        itemDongId: searchParams.get("itemDongId") ?? undefined,
        itemHoId: searchParams.get("itemHoId") ?? undefined,
        itemId: parseOptionalInt(searchParams.get("itemId")),
        disableDecal: parseOptionalBool(searchParams.get("disableDecal")),
        disableFacility: parseOptionalBool(searchParams.get("disableFacility")),
        disableTransparent: parseOptionalBool(searchParams.get("disableTransparent")),
        disableInteriorView: parseOptionalBool(searchParams.get("disableInteriorView")),
        domain: parseOptionalEnum(searchParams.get("domain"), ["zigbang", "hogangnono"] as const),
        areaHoId: parseOptionalInt(searchParams.get("areaHoId")),
      },
      onDanjiViewLoad: onLoad,
    }),
    [searchParams]
  )

  useEffect(() => {
    Sentry.setTag("zed.danjiId", danjiViewProps.danjiId)
    Sentry.setTag("zed.domain", danjiViewProps.danjiOptions?.domain)
    Sentry.setTag("app.version", danjiViewProps.appVersion)
    Sentry.setTag(
      "device.memory",
      danjiViewProps.deviceInfo?.deviceMemory && Math.floor(danjiViewProps.deviceInfo.deviceMemory)
    )
    Sentry.setTag("gpu.name", danjiViewProps.deviceInfo?.gpuModel)
    Sentry.setTag("gpu.version", danjiViewProps.deviceInfo?.openglVersion)
    danjiViewProps.danjiOptions && Sentry.setContext("zed.danjiOptions", danjiViewProps.danjiOptions)
    danjiViewProps.deviceInfo && Sentry.setContext("zed.deviceInfo", danjiViewProps.deviceInfo)
  }, [danjiViewProps])

  return danjiViewProps.danjiId ? <DanjiView {...danjiViewProps} /> : <LoadingPanel />
}

export default function Index() {
  const params = new URLSearchParams(useSearchParams().toString());
  params.set('stage', process.env['STAGE']!);
  params.set('identityPoolId', process.env['COGNITO_IDENTITY_POOL_ID']!);
  params.set('appId', process.env['PINPOINT_APPID']!);
  params.set('region', process.env['PINPOINT_REGION']!);  
  params.set('zedVersion', process.env['NEXT_PUBLIC_ZED_APT3D_VERSION']!);

  initializePinpoint(params);

  return (
    <>
      <Head>
        <title>APT3D</title>
        <meta name="description" content="Item location" />
        <meta name="viewport" content="width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <main className={styles.main}>
        <MainViewer />
      </main>
    </>
  )
}
